import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setShowToast, verifyEmail } from "../../redux/slice/rfpModuleSlice/application";
import { AppDispatch, RootState } from "redux/store";
import Button from "style-guide/component/Button";
import Gradient from "style-guide/component/Gradient";
import Typography from "style-guide/component/Typography";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const token = searchParams.get("token");
  const { isLoadingModal } = useSelector(
    (state: RootState) => state.applicationData
  );

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleVerifyEmail = async () => {
    if (!token) return;
    try {
      const response = await dispatch(verifyEmail(token));
      if (response === 200) {
        setStatus("success");
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Email verification successful!",
          })
        );
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <Gradient>
      <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-xl mx-auto text-center flex flex-col items-center space-y-6">
        {status === "idle" && (
          <>
            <MailOutlined className="text-blue-600 text-6xl" />
            <Typography
              text="Please verify your email address"
              variant="Heading 3"
              className="text-blue-900 font-bold"
            />
            <Typography
              variant="Base"
              className="text-gray-600 max-w-md"
              text="Click the button below to confirm your account and unlock all features."
            />
            <Button
              type={token ? "Primary" : "Disabled"}
              onClick={handleVerifyEmail}
              buttonText={isLoadingModal ? "Verifying..." : "Verify Email"}
              loading={isLoadingModal}
              className="w-full py-3 rounded-lg"
            />
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircleOutlined className="text-green-500 text-7xl animate-bounce" />
            <Typography
              text="Your email has been successfully verified!"
              variant="Heading 3"
              className="text-green-700 font-bold"
            />
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 w-full max-w-md space-y-2">
              <div className="flex items-center gap-2">
                <SafetyCertificateOutlined className="text-green-600 text-xl" />
                <Typography
                  variant="Base"
                  className="text-gray-700"
                  text="Your account is now fully activated."
                />
              </div>
              <div className="flex items-center gap-2">
                <SafetyCertificateOutlined className="text-green-600 text-xl" />
              <Typography
                variant="Base"
                className="text-gray-700"
                text="You can now log in and start exploring all features."
              />
              </div>
            </div>
            <Button
              type="Primary"
              buttonText="Go to Login"
              onClick={() => navigate("/")}
              className="w-full py-3 rounded-lg"
            />
          </>
        )}

        {status === "error" && (
          <>
            <CloseCircleOutlined className="text-red-500 text-7xl" />
            <Typography
              text="Verification failed"
              variant="Heading 3"
              className="text-red-700 font-bold"
            />
            <Typography
              variant="Base"
              className="text-gray-600 max-w-md"
              text="Your verification link may have expired or is invalid. Please request a new verification email."
            />
            <Button
              type="Primary"
              buttonText="Try Again"
              onClick={handleVerifyEmail}
              className="w-full py-3 rounded-lg"
              loading={isLoadingModal}
            />
          </>
        )}
      </div>
    </Gradient>
  );
};

export default VerifyEmail;
